import {
  Component,
  OnInit,
  Input,
  OnDestroy,
  ViewChild,
  EventEmitter,
  Output,
} from "@angular/core";
import {
  PathTest,
  PathTestPoint,
  Path,
  activity_status,
  Stage,
  DriveTest,
  ScoutPath,
  ScoutPoint,
} from "../../core/models";
import { BsModalRef, BsModalService } from "ngx-bootstrap/modal";
import {
  BuildingService,
  DriveTestService,
  extractPathNumberFromPathId,
  PathService,
  PathTestService,
  PathWaypointDoc,
  PathWaypointService,
  RouteService,
  ScoutPointService,
  UserService,
} from "../../core/services";
import StatusId = activity_status.Id;

import { FesBaseComponent } from "../../shared/components/fes-base.component";
import { AppState } from "../../core";
import {
  PathTestDoc
} from "../../core/schema/path-test-schema";
import { NgForm } from "@angular/forms";
import { v4 as uuidv4 } from "uuid";
import { asyncScheduler, Subscription } from "rxjs";
import { StageService } from "../../core/services/stage.service";
import { StageDoc } from "../../core/schema/stage-schema";
import { ConfirmModalComponent } from "../../shared/components/confirm-modal/confirm-modal.component";
import { first } from "rxjs/operators";
import {
  TableViewQueryCriteria,
  TableViewQueryResponse,
} from "../../shared/components/table-view/table-view-component-base";
import { DriveTestDoc } from "../../core/schema/drive-test-schema";

@Component({
  selector: "app-path-test-edit",
  templateUrl: "./path-test-edit.component.html",
  styleUrls: ["./path-test-edit.component.scss"],
})
export class PathTestEditComponent
  extends FesBaseComponent
  implements OnInit, OnDestroy
{
  @Input() set pathTestDoc(pathTestDoc: PathTestDoc | DriveTestDoc) {
    this.setPathTestDoc(pathTestDoc);
  }

  @Input() doAfterSaveFn: () => void;

  @Output() deleted$ = new EventEmitter<PathTestDoc | DriveTestDoc>();

  @ViewChild("pathTestForm") pathTestForm: NgForm;

  pointIds: string[];

  _pathTestDoc: PathTestDoc | DriveTestDoc;
  tmpPathTest: PathTest | DriveTest;

  get pathTestDoc() {
    return this._pathTestDoc;
  }

  buildingInfoArray: { id: string; name: string }[] = [];
  routeInfoArray: { id: string; market: string }[] = [];
  userInfoArray: { id: string; name: string }[] = [];

  @Input() set userNamesById(namesById: { [key: string]: string }) {
    this.userInfoArray = Object.entries(namesById).map(([id, name]) => ({
      id,
      name,
    }));
    this.userInfoArray.sort((a, b) => (a?.name > b?.name ? 1 : -1));
  }

  @Input() set buildingNamesById(namesById: { [key: string]: string }) {
    this.buildingInfoArray = Object.entries(namesById).map(([id, name]) => ({
      id,
      name,
    }));
    this.buildingInfoArray.sort((a, b) => (a?.id > b?.id ? 1 : -1));
  }
  @Input() set routeMarketsById(marketsById: { [key: string]: string }) {
    this.routeInfoArray = Object.entries(marketsById).map(([id, market]) => ({
      id,
      market,
    }));
    this.routeInfoArray.sort((a, b) => (a?.id > b?.id ? 1 : -1));
  }

  @Input() stages: string[] = [];
  @Input() pathIds: string[] = [];

  bProcessing = false;
  bLoadingPointIds = false;
  bLoadingPathIds = false;
  bNoWaypointsFound = false;
  pointsForPathSubscription: Subscription;
  scoutPathsSubscription: Subscription;
  scoutPointByPointId: { [ptId:string]: ScoutPoint} = {};
  testPointsModified = false;
  errorMsg: string = "";

  statusByPointId: { [pointId: string]: StatusId };
  canToggleByStatusId = {
    [StatusId.NOT_USING]: true,
    [StatusId.NOT_STARTED]: true,
  };

  constructor(
    appState: AppState,
    protected editModal: BsModalRef,
    private pathWaypointService: PathWaypointService,
    private _pathTestService: PathTestService,
    private _driveTestService: DriveTestService,
    private userService: UserService,
    private buildingService: BuildingService,
    private routeService: RouteService,
    private pathService: PathService,
    private stageService: StageService,
    private modalService: BsModalService,
    private scoutPointService: ScoutPointService,
  ) {
    super(appState);
  }
  pathTestService: PathTestService | DriveTestService;
  ngOnInit(): void {
    this.bProcessing = true;

    if (this.pathTestDoc.type === "drive test") {
      this.pathTestService = this._driveTestService;
    } else if (this.pathTestDoc.type === "path test") {
      this.pathTestService = this._pathTestService;
    }

    if (!this.userInfoArray?.length) {
      this.subscribe(this.userService.rxCollection.find().$, (users) => {
        this.userNamesById = users.reduce((cache, user) => {
          cache[user._id] = user.firstName + " " + user.lastName;
          return cache;
        }, {});
      });
    }
    if (!this.buildingInfoArray?.length) {
      this.subscribe(
        this.buildingService.rxCollection.find().$,
        (buildings) => {
          this.buildingNamesById = buildings.reduce((cache, building) => {
            cache[building.id] = building.commonName;
            return cache;
          }, {});
        }
      );
    }
    if (!this.routeInfoArray?.length) {
      this.subscribe(this.routeService.rxCollection.find().$, (routes) => {
        this.routeMarketsById = routes.reduce((cache, route) => {
          cache[route.id] = route.market;
          return cache;
        }, {});
      });
    }
    const stageQuery = this.stageService.rxCollection.find();
    this.subscribe(
      stageQuery.$,
      (stages: StageDoc[]) => (this.stages = stages.map((value) => value.name))
    );

    asyncScheduler.schedule(async () => {
      try {
        await this.findPathIdsForBuilding();
        await this.initPointsForPathQuery();
      } catch (error) {
        console.error(error);
      }
      this.bProcessing = false;
    });
    this.subscribe(this.pathTestService.deleted$, (pathTestDoc) => {
      if (pathTestDoc && pathTestDoc._id == this._pathTestDoc?._id) {
        this.deleted$.emit(this._pathTestDoc);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy();
    this.pointsForPathSubscription?.unsubscribe();
    this.scoutPathsSubscription?.unsubscribe();
  }

  setPathTestDoc(pathTestDoc: PathTestDoc | DriveTestDoc) {
    this._pathTestDoc = pathTestDoc;
    this.tmpPathTest = pathTestDoc.toJSON();
    if (!this.tmpPathTest.assignments) {
      this.tmpPathTest.assignments = {
        scheduleUserId: null,
        testUserId: null,
      };
    }

    this.tmpPathTest.assignments.scheduleUserId = this.appState.getUser()._id;
    this.tmpPathTest.updatedUserId = this.appState.getUser()._id;
    this.tmpPathTest.updatedAt = Date.now().toString();
    if (!this.tmpPathTest.schedule) {
      this.tmpPathTest.schedule = {
        scheduledDate: null,
        scheduleNotes: null,
        scheduleComments: null,
      };
    }
    if (!this.tmpPathTest.cycle) {
      this.tmpPathTest.cycle = 1;
    }
    if(!this.tmpPathTest.groupingfield)
      {
        this.tmpPathTest.groupingfield="";
      }
    if (!this.tmpPathTest.stage) {
      this.tmpPathTest.stage = Stage.DefaultStageName;
    }
    if (!this.tmpPathTest.testPoints) {
      this.tmpPathTest.testPoints = [];
    }
    if (this.tmpPathTest._id) {
      this.pointIds = this.tmpPathTest.testPoints.map((tp) => tp.pointId);
    }
    this.testPointsModified =
      !this.tmpPathTest._id || !this.tmpPathTest.testPoints?.length;

    this.statusByPointId = this.tmpPathTest.testPoints.reduce(
      (statusById, tp) => {
        statusById[tp.pointId] = tp.statusId;
        return statusById;
      },
      {}
    );

    if (this.testPointsModified) {
      asyncScheduler.schedule(() => {
        this.pathTestForm?.form?.markAsDirty();
      });
    }
  }

  async findPathIdsForBuilding() {
    this.pathIds = [];
    this.pointsForPathSubscription?.unsubscribe();

    if (!this.tmpPathTest?.buildingId) {
      return;
    }

    this.bLoadingPathIds = true;

    try {
      const queryCriteria: TableViewQueryCriteria = {
        filters: [
          { mode: "=", key: "buildingid", value: this.tmpPathTest.buildingId },
        ],
        sorts: [{ key: "id", order: "ASC" }],
      };
      const result: any = await this.pathService.http
        .get(`${this.pathService.urlPath}/find`, {
          params: { criteria: JSON.stringify(queryCriteria) },
        })
        .toPromise();
      const resp: TableViewQueryResponse<Path> = result.data;
      this.pathIds = resp.rows.map((row) => row.id);
    } catch (error) {
      console.error(error);
      this.pathWaypointService.publishError(error, this);
    }
    this.bLoadingPathIds = false;
  }
  async findPathIdsForRoute() {
    this.pathIds = [];
    this.pointsForPathSubscription?.unsubscribe();

    if (!this.tmpPathTest?.routeId) {
      return;
    }

    this.bLoadingPathIds = true;

    try {
      const queryCriteria: TableViewQueryCriteria = {
        filters: [
          { mode: "=", key: "routeid", value: this.tmpPathTest.routeId },
        ],
        sorts: [{ key: "id", order: "ASC" }],
      };
      const result: any = await this.pathService.http
        .get(`${this.pathService.urlPath}/find`, {
          params: { criteria: JSON.stringify(queryCriteria) },
        })
        .toPromise();
      const resp: TableViewQueryResponse<Path> = result.data;
      this.pathIds = resp.rows.map((row) => row.id);
    } catch (error) {
      console.error(error);
      this.pathWaypointService.publishError(error, this);
    }
    this.bLoadingPathIds = false;
  }

  async initPointsForPathQuery() {
    this.pointsForPathSubscription?.unsubscribe();
    this.pointIds = [];
    if (!this.tmpPathTest.pathId) {
      return;
    }

    const pathId = this.tmpPathTest.pathId;
    const pathNumber = extractPathNumberFromPathId(pathId);

    this.scoutPathsSubscription?.unsubscribe();
    const scoutPtInfos = await this.scoutPointService.buildScoutInfoForBuildingAndPath({
      buildingId: this.tmpPathTest.buildingId,
      pathId: this.tmpPathTest.pathId,
      stage: this.tmpPathTest.stage,
    });
    const scoutTaskDoc = scoutPtInfos[0]?.scout;
    if ( scoutTaskDoc ) {
      this.scoutPathsSubscription = scoutTaskDoc.get$('scoutPaths').subscribe((scoutPaths:ScoutPath[]) => {
        const scoutPath = scoutPaths.find(sp=>sp.pathNumber===pathNumber)
        if ( scoutPath ) {
          this.scoutPointByPointId = scoutPath.scoutPoints.reduce( (infoById,sp)=>{
            infoById[sp.pointId||sp.exPointId] = sp;
            return infoById
          },{});
        } else {
          this.scoutPointByPointId = {}
        }
      });    
    }
    let waypointsQuery = this.pathWaypointService.rxCollection
      .find()
      .where("pathId")
      .eq(pathId)
      .where("type")
      .regex(/^(HA|EX)$/i);
    this.bLoadingPointIds = true;
    this.pointsForPathSubscription = waypointsQuery.$.subscribe(
      (waypoints: PathWaypointDoc[]) => {
        if (pathId == this.tmpPathTest.pathId) {
          this.pointIds = waypoints.map((wp) => wp.pointId);
          this.statusByPointId = this.pointIds.reduce((sById, ptId) => {
            sById[ptId] = this.statusByPointId[ptId] || StatusId.NOT_STARTED;
            return sById;
          }, {});
        }
        this.bLoadingPointIds = false;
      }
    );
  }

  async onDelete() {
    this.errorMsg = "";
    try {
      await this.pathTestService.deletePathTestModal(
        this.modalService,
        this.pathTestDoc
      );
    } catch (error) {
      this.errorMsg = error.message;
    }
  }

  async onSave() {
    this.errorMsg = "";
    this.bProcessing = true;
    const updateStatusId = (testPoint:PathTestPoint,pointId: string) => {
      testPoint.statusId = this.statusByPointId[pointId];
      if ( testPoint.statusId === undefined ) {
        testPoint.statusId = activity_status.Id.NOT_STARTED;
      }
      if ( testPoint.statusId === activity_status.Id.NOT_STARTED && this.scoutPointByPointId[pointId]?.disabled) {
        testPoint.statusId = activity_status.Id.NOT_USING;
      }
    }

    try {
      if (this.tmpPathTest.type == "path test"){
        if ( this.tmpPathTest.order === undefined ) {
          this.tmpPathTest.order = null
        }
      }
      if (!this.pathTestDoc._id) {
        // create new path test

        if (this.tmpPathTest.type == "drive test") {
          this.pathTestDoc.routeId = this.tmpPathTest.routeId;
          this.pathTestDoc.updatedAt = new Date().toISOString();
          this.pathTestDoc.updatedUserId = this.tmpPathTest.updatedUserId;
        } else if (this.tmpPathTest.type == "path test") {
          this.pathTestDoc.buildingId = this.tmpPathTest.buildingId;
          this.pathTestDoc.pathId = this.tmpPathTest.pathId;
          this.pathTestDoc.order = this.tmpPathTest.order;
          this.pointIds.forEach((pointId, index) => {
            let testPoint = new PathTestPoint();
            testPoint.id = uuidv4();
            testPoint.pointId = pointId;
            testPoint.order = index + 1;
            testPoint.statusId = this.statusByPointId[pointId];
            testPoint.seqs = [];
            this.tmpPathTest.testPoints.push(testPoint);
            updateStatusId(testPoint,pointId);
          });
          this.pathTestDoc.testPoints = this.tmpPathTest.testPoints;
        }
        this.pathTestDoc.cycle = this.tmpPathTest.cycle;
        this.pathTestDoc.stage = this.tmpPathTest.stage;
        this.pathTestDoc.assignedUserId =
          this.tmpPathTest.assignments.testUserId;
        this.pathTestDoc.assignments = this.tmpPathTest.assignments;
        this.pathTestDoc.schedule = this.tmpPathTest.schedule;
        this.pathTestDoc.groupingfield = this.tmpPathTest.groupingfield;


        await this.pathTestDoc.save();
        this.pathTestService.syncNow();
      } else {
        // edit existing path test
        await this.pathTestDoc.atomicUpdate((doc) => {
          if (this.tmpPathTest.type == "drive test") {
            doc.routeId = this.tmpPathTest.routeId;
            doc.updatedAt = new Date().toISOString();
            doc.updatedUserId = this.tmpPathTest.updatedUserId;
          } else if (this.tmpPathTest.type == "path test") {
            doc.buildingId = this.tmpPathTest.buildingId;
            doc.order = this.tmpPathTest.order;
            if (this.testPointsModified) {
              const oldTestPoints = this.pathTestDoc.testPoints;

              doc.testPoints = [];
              this.pointIds.forEach((pointId, index) => {
                let testPoint = oldTestPoints.find(
                  (tp) => tp.pointId == pointId
                );
                if (!testPoint) {
                  testPoint = new PathTestPoint();
                  testPoint.pointId = pointId;
                  testPoint.statusId = StatusId.NOT_STARTED;
                  testPoint.seqs = [];
                }
                testPoint.order = index + 1;
                if (!testPoint.id) {
                  testPoint.id = uuidv4();
                }
                updateStatusId(testPoint,pointId);   
                doc.testPoints.push(testPoint);
              });
            }
          }
          doc.cycle = this.tmpPathTest.cycle;
          doc.stage = this.tmpPathTest.stage;
          doc.assignedUserId = this.tmpPathTest.assignments.testUserId;
          doc.assignments = this.tmpPathTest.assignments;
          doc.schedule = this.tmpPathTest.schedule;
          doc.groupingfield=this.tmpPathTest.groupingfield;
          return doc;
        });
      }
      this.editModal.hide();
      this.doAfterSaveFn && this.doAfterSaveFn();
      if (
        (await this.findPathTestsForBuildingCycle()).length > 1 &&
        this.tmpPathTest.type == "path test"
      ) {
        await this.confirmRandomizePathTests();
      }
    } catch (error) {
      this.errorMsg = this.pathTestService.extractErrorMsg(error);
      console.error(error);
      this.pathTestService.publishError(error, this);
    } finally {
      this.pathTestService.syncNow();
      this.bProcessing = false;
    }
  }

  onCancel() {
    this.editModal.hide();
  }

  async onBuildingIdChanged(buildingId: string) {
    await this.findPathIdsForBuilding();
  }

  onPathIdChanged(pathId: string) {
    //this.pointIds = this.pathIdInfoArray.find(pathIdInfo=>pathIdInfo.id==pathId)?.pointIds
    this.initPointsForPathQuery();
  }

  randomizePoints() {
    const groups = this.pointIds.groupBy((id: string) =>
      this.statusByPointId[id] == StatusId.NOT_USING ? "notUsing" : "using"
    );
    this.pointIds = groups.using || [];
    const notUsing = groups.notUsing || [];
    for (let i = this.pointIds.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [this.pointIds[i], this.pointIds[j]] = [
        this.pointIds[j],
        this.pointIds[i],
      ];
    }
    this.pointIds.push(...notUsing);
    this.testPointsModified = true;
    this.pathTestForm.form.markAsDirty();
  }

  async findPathTestsForBuildingCycle() {
    let pathTests;
    if (this.tmpPathTest.type === "drive test") {
      pathTests = (
        await this.pathTestService.rxCollection
          .find({
            selector: {
              routeId: this.pathTestDoc.routeId,
              cycle: this.pathTestDoc.cycle,
              stage: this.pathTestDoc.stage,
            },
          })
          .exec()
      ).sort((a, b) =>
        a?.pathId > b?.pathId ? 1 : b?.pathId > a?.pathId ? -1 : 0
      );
    } else if (this.tmpPathTest.type === "path test") {
      pathTests = (
        await this.pathTestService.rxCollection
          .find({
            selector: {
              buildingId: this.pathTestDoc.buildingId,
              cycle: this.pathTestDoc.cycle,
              stage: this.pathTestDoc.stage,
            },
          })
          .exec()
      ).sort((a, b) =>
        a?.pathId > b?.pathId ? 1 : b?.pathId > a?.pathId ? -1 : 0
      );
    }
    return pathTests;
  }

  async confirmRandomizePathTests() {
    const pathTests = await this.findPathTestsForBuildingCycle();
    let modalRef: BsModalRef = null;
    if (pathTests.length <= 1) {
      modalRef = this.modalService.show(ConfirmModalComponent, {
        ignoreBackdropClick: true,
        id: 20220107,
        class: "modal-lg",
        initialState: {
          title: "Randomize Not Applicable",
          question: `Cannot randomize. Two or more paths needed for ${
            this.pathTestDoc.stage
          } ${this.pathTestDoc.buildingId} cycle ${this.pathTestDoc.cycle}.
            <small><div class="d-flex flex-wrap justify-content-center mt-2 font-italic">
            ${pathTests
              .map(
                (pt) => '<div class="group-path-name">' + pt.pathId + "</div>"
              )
              .join("")}</div></small>`,
          focusOnNo: true,
          yesBtnText: "Close",
          noBtnText: null,
        },
      });
    } else {
      modalRef = this.modalService.show(ConfirmModalComponent, {
        ignoreBackdropClick: true,
        id: 20220107,
        class: "modal-lg",
        initialState: {
          onYes: async () => {
            await this.randomizePathTests(pathTests);
          },
          title: "Randomize Path Tests",
          question: `Randomize ${pathTests.length} path test(s) for ${
            this.pathTestDoc.stage
          } ${this.pathTestDoc.buildingId} cycle ${this.pathTestDoc.cycle}?
            <small><div class="d-flex flex-wrap justify-content-center mt-2 font-italic">
            ${pathTests
              .map(
                (pt) => '<div class="group-path-name">' + pt.pathId + "</div>"
              )
              .join("")}</div></small>`,
          focusOnNo: true,
        },
      });
    }
    await modalRef.onHidden.pipe(first()).toPromise();
  }

  private async randomizePathTests(
    srcPathTests: PathTestDoc[] | DriveTestDoc[]
  ) {
    console.log(
      `randomizePathTests(${srcPathTests?.map(
        (pt) => pt.pathId + " #" + pt.order
      )})`
    );
    const pathTests = [...srcPathTests];
    for (let i = pathTests.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [pathTests[i], pathTests[j]] = [pathTests[j], pathTests[i]];
    }
    try {
      for (let i = 0; i < pathTests.length; i++) {
        await pathTests[i].atomicPatch({ order: i + 1 });
      }
      this.tmpPathTest.order = this.pathTestDoc.order;
      this.doAfterSaveFn && this.doAfterSaveFn();
    } catch (error: any) {
      this.pathTestService.publishErrorObj(
        error,
        "Randomize paths error occured",
        this
      );
    }
  }

  // test points getting reloaded in dialog causes issue with events bindings so use trackBy
  testPointPointId(index, testPoint) {
    return testPoint?.pointId;
  }

  toggleNotUsing(pointId: string) {
    if (!this.canToggleByStatusId[this.statusByPointId[pointId]]) {
      return;
    }
    const docTestPts = this.tmpPathTest.testPoints;
    const docPtStatusId =
      docTestPts?.find((pt) => pt.pointId == pointId)?.statusId ||
      StatusId.NOT_USING;
    this.statusByPointId[pointId] =
      this.statusByPointId[pointId] != StatusId.NOT_USING
        ? StatusId.NOT_USING
        : docPtStatusId == StatusId.NOT_USING
        ? StatusId.NOT_STARTED
        : docPtStatusId;
    this.testPointsModified = true;
    this.pathTestForm.form.markAsDirty();
  }
}
